import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { getAllRoles, createRole, getRoleByName } from "../../api/roles";

export default function RolesAdmin() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [queryName, setQueryName] = useState("");
  const [foundRole, setFoundRole] = useState(null);

  const canCreate = useMemo(
    () => newName.trim().length > 0 && newDesc.trim().length > 0,
    [newName, newDesc]
  );

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAllRoles();
      const arr = Array.isArray(data) ? data : [data];
      setRoles(arr);
    } catch (e) {
      setError("Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!canCreate) return;
    setLoading(true);
    setError("");
    try {
      await createRole({ name: newName.trim(), description: newDesc.trim() });
      setSuccess("Role created");
      setNewName("");
      setNewDesc("");
      await load();
    } catch (e) {
      setError("Failed to create role");
    } finally {
      setLoading(false);
    }
  };

  const handleFind = async () => {
    if (!queryName.trim()) return;
    setLoading(true);
    setError("");
    setFoundRole(null);
    try {
      const data = await getRoleByName(queryName.trim());
      setFoundRole(data);
    } catch (e) {
      setError("Role not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Roles Management
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                All Roles
              </Typography>
              <Button
                onClick={load}
                disabled={loading}
                sx={{ mb: 2 }}
                variant="outlined"
              >
                Refresh
              </Button>
              <List dense>
                {roles.map((r, i) => (
                  <ListItem key={`${r?.id || i}-${r?.name || i}`}>
                    <ListItemText
                      primary={r?.name || "-"}
                      secondary={r?.description || ""}
                    />
                  </ListItem>
                ))}
                {roles.length === 0 && (
                  <Typography variant="body2">No roles</Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Create Role
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    label="Name"
                    fullWidth
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    label="Description"
                    fullWidth
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={!canCreate || loading}
                  >
                    Create
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Find Role By Name
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    label="Name"
                    fullWidth
                    value={queryName}
                    onChange={(e) => setQueryName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={handleFind}
                    disabled={loading || !queryName.trim()}
                  >
                    Find
                  </Button>
                </Grid>
                {foundRole && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2">
                      Name: {foundRole?.name}
                    </Typography>
                    <Typography variant="body2">
                      Description: {foundRole?.description}
                    </Typography>
                    <Typography variant="body2">
                      Active: {String(foundRole?.active)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
        message={error}
      />
      <Snackbar
        open={!!success}
        autoHideDuration={2500}
        onClose={() => setSuccess("")}
        message={success}
      />
    </Box>
  );
}
